import { useCallback, useEffect, useState } from "react"
import { useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"

import { currentBuildId, subscribeToUpdates } from "src/utils/serviceWorker"

import "./UpdateBanner.css"

// One reload per build, per tab. The page that is running still carries the *old* build's id, so
// storing it is a note saying "I have already reloaded as this build": if the new worker somehow
// serves the same shell again, the banner says so once more and the page does not spin.
export const RELOADED_FOR_KEY = "hvz-update-reloaded"

// A lobby seat is held by the socket, and a reload drops it — the player comes back to a room that
// has forgotten them. Everywhere else a reload is free: the game's state is the server's and the
// socket reconnects into it.
export const isSeatedPath = (pathname) => pathname.startsWith("/lobby/")

export const reloadForUpdate = () => {
  const id = currentBuildId()
  try {
    if (window.sessionStorage.getItem(RELOADED_FOR_KEY) === id) return false
    window.sessionStorage.setItem(RELOADED_FOR_KEY, id ?? "")
  } catch (error) {
    // Private mode, or storage the browser refuses. The guard is a courtesy against a reload loop,
    // not the feature — without it the reload still happens, which is what the player asked for.
  }
  window.location.reload()
  return true
}

/* "A new version is ready", once the worker on disk has actually changed (C1–C3 of
   requirements/29). The two halves this is the visible end of: every deploy is now a different
   worker (`public/sw.js`'s stamped BUILD_ID), and an open app asks for one
   (`utils/serviceWorker`).

   RoundFlash's style, because it is the same kind of thing — a caption the app says about itself
   — but this one waits for a press rather than leaving on a timer, since the player may be
   mid-round and the whole point is that they choose when the page goes away.

   **It reloads by itself the moment the app is put away.** On a phone that is the next time the
   game is backgrounded, which is the one moment no player is mid-action; coming back, they are on
   the new version and never saw a banner about it. Never while they are looking at the screen, and
   never in a lobby — a reload there drops the seat, so there the banner waits for the button. */
const UpdateBanner = () => {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const [ready, setReady] = useState(false)

  useEffect(() => subscribeToUpdates(() => setReady(true)), [])

  const seated = isSeatedPath(pathname)

  useEffect(() => {
    if (!ready || seated) return undefined
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") reloadForUpdate()
    }
    document.addEventListener("visibilitychange", onVisibilityChange)
    return () => document.removeEventListener("visibilitychange", onVisibilityChange)
  }, [ready, seated])

  const update = useCallback(() => reloadForUpdate(), [])

  if (!ready) return null

  return (
    <div className="update-banner" role="status" aria-live="polite">
      <span className="update-banner-label">{t("app.updateAvailable")}</span>
      <button type="button" className="update-banner-action" onClick={update}>
        {t("app.updateNow")}
      </button>
    </div>
  )
}

export default UpdateBanner
